import { useCallback, useEffect, useState, type ChangeEvent } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import { api } from '../../../shared/services/API'
import { useNotification } from '../../../shared/contexts/NotificationContext'
import { getBackendError } from '../../../shared/utils/formErrors'
import type { Page } from '../../../shared/utils/types'
import { useTablePagination } from '../../../shared/hooks/useTablePagination'
import { formatDate, type PlatformAdminItem } from '../adminModel'
import { PLATFORM_ROLE_LABEL } from '../platformRoles'
import { usePlatformAuth } from '../context/PlatformAuthContext'
import AdminFormDialog from '../components/AdminFormDialog'
import PasswordDialog from '../components/PasswordDialog'

export default function AdminsList() {
    const { notify } = useNotification()
    const { refreshMe } = usePlatformAuth()

    const [rows, setRows] = useState<PlatformAdminItem[]>([])
    const [total, setTotal] = useState(0)
    const { page, setPage, rowsPerPage, setRowsPerPage } = useTablePagination('platform-admins:table-page', 20)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    const [formOpen, setFormOpen] = useState(false)
    const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
    const [editing, setEditing] = useState<PlatformAdminItem | null>(null)
    const [resetTarget, setResetTarget] = useState<PlatformAdminItem | null>(null)

    const fetchAdmins = useCallback(() => {
        setIsLoading(true)
        setError('')
        api.get<Page<PlatformAdminItem>>('/api/admin/platform-admins', {
            params: { page, size: rowsPerPage, sort: 'nome,asc' },
        })
            .then((r) => {
                setRows(r.data.content ?? [])
                setTotal(r.data.totalElements ?? 0)
            })
            .catch(() => setError('Não foi possível carregar os administradores.'))
            .finally(() => setIsLoading(false))
    }, [page, rowsPerPage])

    useEffect(() => { fetchAdmins() }, [fetchAdmins])

    const handleChangePage = (_: unknown, next: number) => setPage(next)
    const handleChangeRowsPerPage = (e: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(e.target.value, 10))
        setPage(0)
    }

    const openCreate = () => { setFormMode('create'); setEditing(null); setFormOpen(true) }
    const openEdit = (a: PlatformAdminItem) => { setFormMode('edit'); setEditing(a); setFormOpen(true) }

    const handleDelete = async (a: PlatformAdminItem) => {
        if (!window.confirm(`Remover o administrador "${a.nome}" (${a.email})? Esta acção não pode ser desfeita.`)) return
        try {
            await api.delete(`/api/admin/platform-admins/${a.id}`)
            notify('Administrador removido.')
            fetchAdmins()
        } catch (err) {
            notify(getBackendError(err)?.message ?? 'Não foi possível remover.', 'error')
        }
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>Administradores da plataforma</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {total} {total === 1 ? 'administrador' : 'administradores'}
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    Novo administrador
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Papéis</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell>Criado em</TableCell>
                                <TableCell align="right">Acções</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={24} /></TableCell>
                                </TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                                        Ainda não há administradores.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((a) => (
                                    <TableRow key={a.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {a.nome}
                                            {a.proprio && (
                                                <Chip label="Você" size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{a.email}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {a.roles.length === 0 ? (
                                                    <Typography variant="caption" color="text.secondary">—</Typography>
                                                ) : (
                                                    a.roles.map((r) => (
                                                        <Chip key={r} label={PLATFORM_ROLE_LABEL[r] ?? r} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                                    ))
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={a.activo ? 'Activo' : 'Inactivo'}
                                                sx={{
                                                    fontWeight: 600,
                                                    bgcolor: a.activo ? '#F0FDF4' : '#F3F4F6',
                                                    color: a.activo ? '#15803D' : '#6B7280',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{formatDate(a.criadoEm)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar">
                                                <IconButton size="small" onClick={() => openEdit(a)} aria-label="Editar">
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Redefinir palavra-passe">
                                                <IconButton size="small" onClick={() => setResetTarget(a)} aria-label="Redefinir palavra-passe">
                                                    <KeyOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={a.proprio ? 'Não pode remover a sua própria conta' : 'Remover'}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: 'error.main' }}
                                                        disabled={a.proprio}
                                                        onClick={() => handleDelete(a)}
                                                        aria-label="Remover"
                                                    >
                                                        <DeleteForeverOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50]}
                    labelRowsPerPage="Linhas por página"
                />
            </Paper>

            <AdminFormDialog
                open={formOpen}
                mode={formMode}
                initial={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => {
                    setFormOpen(false)
                    notify(formMode === 'create' ? 'Administrador criado.' : 'Administrador actualizado.')
                    fetchAdmins()
                    // Editou a própria conta → refrescar permissões/nav sem F5.
                    if (formMode === 'edit' && editing?.proprio) refreshMe()
                }}
            />

            <PasswordDialog
                open={resetTarget !== null}
                title="Redefinir palavra-passe"
                subtitle={resetTarget ? `Nova palavra-passe para ${resetTarget.nome} (${resetTarget.email}).` : undefined}
                onClose={() => setResetTarget(null)}
                onSaved={() => { setResetTarget(null); notify('Palavra-passe redefinida.') }}
                submit={async ({ novaPassword }) => {
                    await api.put(`/api/admin/platform-admins/${resetTarget!.id}/password`, { novaPassword })
                }}
            />
        </Box>
    )
}
